import React, { useState, useEffect } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "../Components/UI/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../Components/UI/select";
import { Button } from "../Components/UI/Button";
import { GroupedData, IncidentQueryField } from "../types/salesforce";

interface ChartVisualizerProps {
  groupedData: GroupedData[];
  availableFields: IncidentQueryField[];
  level1Field: string;
  level2Field?: string;
}

type ChartType = "bar" | "line" | "pie";

interface ChartConfig {
  type: ChartType;
  data: {
    labels: string[];
    datasets: {
      label: string;
      data: number[];
      backgroundColor?: string;
      borderColor?: string;
      borderWidth?: number;
      fill?: boolean;
      type?: string;
    }[];
  };
  options: {
    title: {
      display: boolean;
      text: string;
    };
    scales?: {
      y: {
        beginAtZero: boolean;
      };
    };
  };
}

const CHART_COLORS = [
  "rgb(54, 162, 235)",
  "rgb(255, 99, 132)",
  "rgb(75, 192, 192)",
  "rgb(255, 205, 86)",
  "rgb(153, 102, 255)",
  "rgb(255, 159, 64)",
];

export const ChartVisualizer: React.FC<ChartVisualizerProps> = ({
  groupedData,
  availableFields,
  level1Field,
  level2Field,
}) => {
  const [chartType, setChartType] = useState<ChartType>("bar");
  const [chartUrl, setChartUrl] = useState<string>("");

  const getFieldLabel = (fieldName: string) => {
    return (
      availableFields.find((f) => f.Field_API_Name__c === fieldName)?.Label ||
      fieldName
    );
  };

  const generateChartConfig = (): ChartConfig => {
    // Ensure data is sorted chronologically for date fields
    const field1 = availableFields.find(
      (f) => f.Field_API_Name__c === level1Field
    );
    const isDateField =
      field1?.Field_Type__c === "Date" || field1?.Field_Type__c === "DateTime";

    const sortedData = isDateField
      ? [...groupedData].sort(
          (a: any, b: any) =>
            new Date(a.periodStart).getTime() -
            new Date(b.periodStart).getTime()
        )
      : groupedData;

    const labels = sortedData.map((g) => g.groupValue);

    if (level2Field) {
      // Get all unique subgroup values
      const allSubgroups = new Set<string>();
      sortedData.forEach((g) => {
        g.subgroups?.forEach((sg) => allSubgroups.add(sg.groupValue));
      });

      const getSubgroupCount = (g: any, subgroupValue: string): number => {
        return (
          g.subgroups?.find((sg: any) => sg.groupValue === subgroupValue)
            ?.count ?? 0
        );
      };

      const datasets = Array.from(allSubgroups).map((subgroupValue, index) => ({
        label: subgroupValue,
        data: sortedData.map((g) => getSubgroupCount(g, subgroupValue)),
        backgroundColor: CHART_COLORS[index % CHART_COLORS.length],
        borderColor:
          chartType === "line"
            ? CHART_COLORS[index % CHART_COLORS.length]
            : "white",
        borderWidth: 1,
        fill: chartType === "line" ? false : undefined,
      }));

      return {
        type: chartType,
        data: { labels, datasets },
        options: {
          title: {
            display: true,
            text: `${getFieldLabel(level1Field)} by ${getFieldLabel(level2Field)}`,
          },
          scales: {
            y: {
              beginAtZero: true,
            },
          },
        },
      };
    } else {
      return {
        type: chartType,
        data: {
          labels,
          datasets: [
            {
              label: getFieldLabel(level1Field),
              data: sortedData.map((g) => g.count),
              backgroundColor: CHART_COLORS[0],
              borderColor: chartType === "line" ? CHART_COLORS[0] : "white",
              borderWidth: 1,
              fill: chartType === "line" ? false : undefined,
            },
          ],
        },
        options: {
          title: {
            display: true,
            text: `Count by ${getFieldLabel(level1Field)}`,
          },
          scales:
            chartType !== "pie"
              ? {
                  y: {
                    beginAtZero: true,
                  },
                }
              : undefined,
        },
      };
    }
  };

  const generateChartUrl = () => {
    const config = generateChartConfig();
    const encodedConfig = encodeURIComponent(JSON.stringify(config));
    return `https://quickchart.io/chart?c=${encodedConfig}&v=2.9.4&w=800&h=400&bkg=white`;
  };

  useEffect(() => {
    if (groupedData.length > 0) {
      setChartUrl(generateChartUrl());
    }
  }, [groupedData, chartType]);

  if (!groupedData.length) return null;

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle>Visualization</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <Select
              value={chartType}
              onValueChange={(value: ChartType) => setChartType(value)}
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Select chart type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="bar">Bar Chart</SelectItem>
                <SelectItem value="line">Line Chart</SelectItem>
                {!level2Field && <SelectItem value="pie">Pie Chart</SelectItem>}
              </SelectContent>
            </Select>

            <Button
              onClick={() => window.open(chartUrl, "_blank")}
              variant="outline"
            >
              Open in New Tab
            </Button>
          </div>

          {chartUrl && (
            <div className="rounded-lg border p-4">
              <img
                src={chartUrl}
                alt="Data Visualization"
                className="h-auto max-w-full"
              />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
